import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  NgZone,
  QueryList,
  ViewChildren,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { Task } from '@shared/models/task.model';
import { TaskStatus } from '@shared/enums/task-status.enum';
import { TaskType } from '@shared/enums/task-type.enum';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { EmployeeSearchItem } from '@shared/models/employee-search-item.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { DndModule } from 'ngx-drag-drop';
import { UIModule } from '@shared/modules/ui/ui.module';
import { SharedModule } from '@shared/shared.module';
import { FloatingPopoverTriggerDirective } from '@shared/components/floating-popover/floating-popover-trigger.directive';
import { ProcessMessageTaskSidenavComponent } from '@modules/projects/modules/project-dashboard/pages/project-stage-tasks/components/process-message-task-sidenav/process-message-task-sidenav.component';
import { Project } from '@modules/projects/shared/models/project.model';
import { ListTaskService } from '@shared/modules/list/services/list-task.service';
import { TaskStatusList } from '@shared/consts/task-status.list';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { Config } from '@shared/configs/config';
import { ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { SnackBarService } from '@core/services/snackbar.service';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import {
  getLatestTaskMeta,
  getTaskMailDeliveryState,
  getProjectMetaInlineLabel,
  getProjectMetaPrimaryLine,
  getProjectMetaProject,
  getProjectMetaSecondaryLine,
  getTaskStatusCssClass,
  getTaskStatusIcon,
  getTaskTypeCssClass,
  getTaskTypeIcon,
  getTaskTypeTranslationKey,
  hasVisibleSubtasks,
  isMessageLikeTaskType,
  normalizeTaskStatus
} from './list-tasks-single-task.helper';

@Component({
  selector: 'list-tasks-single-task',
  templateUrl: './list-tasks-single-task.component.html',
  styleUrls: ['./list-tasks-single-task.component.scss'],
  host: { class: 'list-tasks-single-task-wrapper' },
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NgClass, DndModule, UIModule, SharedModule, FloatingPopoverTriggerDirective]
})
export class ListTasksSingleTaskComponent {
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  protected readonly listTaskService = inject(ListTaskService);
  private readonly dialog = inject(MatDialog);
  private readonly taskService = inject(TaskService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly translateService = inject(TranslateService);
  private interval: ReturnType<typeof setInterval> | null = null;
  private dragging = false;
  private mousePosition = { x: 0, y: 0 };
  protected pendingStatus: TaskStatus | null = null;
  protected statusPanelOpen = false;
  protected cancelScheduledMailLoading = false;

  task = input.required<Task>();
  groupId = input.required<number>();
  isProcessGroup = input<boolean>(false);
  status = input<'created' | 'completed' | 'cancelled'>('created');
  parentTask = input<Task | null>(null);
  hideHierarchyIndicator = input<boolean>(false);
  showRemoveAction = input<boolean>(false);
  simplifiedSubtaskLayout = input<boolean>(false);
  nestingLevel = input<number>(0);

  statusUpdated = output<{ task: Task; subtasksCompleted?: boolean; applyStatusToSubtasks?: boolean }>();
  taskClicked = output<{ task: Task; groupId: number; isProcessGroup: boolean }>();
  removeRequested = output<Task>();
  @ViewChildren('subtasksComponents')
  protected subtasksComponents?: QueryList<ListTasksSingleTaskComponent>;

  protected readonly TaskStatus = TaskStatus;
  protected readonly TaskType = TaskType;
  protected readonly taskSidenavController = new TaskSidenavController();
  protected readonly taskStatusOptions = TaskStatusList;

  protected get isSubtask(): boolean {
    return !!this.parentTask();
  }

  protected get currentTaskStatus(): TaskStatus {
    return normalizeTaskStatus(this.task()?.status);
  }

  protected get shouldShowSubtasksStatusPopover(): boolean {
    return hasVisibleSubtasks(this.task());
  }

  @HostBinding('class.list-tasks-single-task-wrapper--subtask')
  protected get isSubtaskWrapper(): boolean {
    return this.isSubtask;
  }

  @HostBinding('class.list-tasks-single-task-wrapper--opened')
  protected get isOpenedWrapper(): boolean {
    return !!this.task()?.openedSubtasks;
  }

  @HostBinding('class.list-tasks-single-task-wrapper--opened-root')
  protected get isOpenedRootWrapper(): boolean {
    return !!this.task()?.openedSubtasks && !this.isSubtask;
  }

  @HostBinding('class.list-tasks-single-task-wrapper--subtask-parent')
  protected get isSubtaskParentWrapper(): boolean {
    return this.isSubtaskParent;
  }

  protected get isSubtaskParent(): boolean {
    return this.isSubtask && !!this.task()?.childrenCount;
  }

  protected get subtaskLeadingOffset(): number {
    return 66 + Math.max(0, this.nestingLevel() - 1) * 60;
  }

  protected showProcessDeadlineTag = computed<boolean>(
    () => !!this.isProcessGroup() && !!this.task()?.termEnd && !this.shouldShowAnyStatusMeta
  );

  protected isMessageLikeTask = computed<boolean>(() => {
    return isMessageLikeTaskType((this.task() as any)?.type);
  });

  protected isProcessMessageLikeTask = computed<boolean>(
    () => this.isProcessGroup() && this.isMessageLikeTask()
  );

  protected isProcessMessageSent = computed<boolean>(
    () => this.isMessageLikeTask() && !!this.task()?.mailSentAt && !!this.task()?.mailSentBy
  );

  protected isProcessMessageScheduled = computed<boolean>(
    () => this.isMessageLikeTask() && !!this.task()?.mailScheduledAt
  );

  protected isSentProcessMessageLike = computed<boolean>(() => {
    if (!this.isProcessGroup()) return false;
    const type = String(this.task()?.type || '');
    return (
      (type === TaskType.TYPE_MESSAGE || type === TaskType.TYPE_FOLLOW_UP) && this.isProcessMessageSent()
    );
  });

  protected isCompleted = computed<boolean>(() => this.task()?.status === TaskStatus.COMPLETED);

  protected isCancelled = computed<boolean>(() => this.task()?.status === TaskStatus.CANCELLED);

  protected shouldBeDraggable = computed<boolean>(() => {
    if (this.isProcessGroup()) return false;
    return !this.isSubtask && !this.isCompleted() && !this.isCancelled();
  });

  protected get assignedUsers(): (string | EmployeeSearchItem)[] {
    const task = this.task();
    return [...(task?.assignedTo || []), ...(task?.assignedEmails || [])];
  }

  protected get shouldShowMyWorkProjectMeta(): boolean {
    return (
      this.listTaskService.isMyWorkTaskLists &&
      !this.isProcessGroup() &&
      !this.simplifiedSubtaskLayout() &&
      !!this.projectMetaProject?.id
    );
  }

  protected get projectMetaProject(): Project | null {
    return getProjectMetaProject(this.task());
  }

  protected get projectMetaPrimaryLine(): string {
    return getProjectMetaPrimaryLine(this.projectMetaProject);
  }

  protected get projectMetaSecondaryLine(): string {
    return getProjectMetaSecondaryLine(this.projectMetaProject, this.projectMetaPrimaryLine);
  }

  protected get projectMetaInlineLabel(): string {
    return getProjectMetaInlineLabel(this.projectMetaPrimaryLine, this.projectMetaSecondaryLine);
  }

  protected get shouldShowAnyStatusMeta(): boolean {
    return !!this.latestTaskMeta;
  }

  protected get latestTaskMetaDate(): string | null {
    return this.latestTaskMeta?.date || null;
  }

  protected get latestTaskMetaLabelKey(): string {
    return this.latestTaskMeta?.labelKey || '';
  }

  protected get latestTaskMetaIsCancelled(): boolean {
    return !!this.latestTaskMeta?.isCancelled;
  }

  protected get latestTaskMetaActorName(): string {
    return this.latestTaskMeta?.actorName || '';
  }

  protected get latestTaskMetaActorAvatar(): string | null {
    return this.latestTaskMeta?.actorAvatar || null;
  }

  protected get shouldShowCancelScheduledMailAction(): boolean {
    return this.latestTaskMeta?.type === 'scheduled' && !!this.task()?.id;
  }

  protected get latestTaskMeta() {
    return getLatestTaskMeta(this.task());
  }

  protected get taskMailDeliveryState() {
    return getTaskMailDeliveryState(this.task());
  }

  protected get shouldShowTaskMailDeliveryState(): boolean {
    return (
      this.isMessageLikeTask() &&
      !!this.taskMailDeliveryState &&
      this.latestTaskMeta?.type === 'sent' &&
      !this.simplifiedSubtaskLayout()
    );
  }

  protected get taskMailDeliveryTooltip(): string {
    const deliveryState = this.taskMailDeliveryState;

    if (!deliveryState?.date) return '';

    return `${this.translateService.instant(deliveryState.labelKey)}: ${this.formatMetaDate(
      deliveryState.date
    )}`;
  }

  protected get taskMailDeliveryPopoverContext(): Record<string, unknown> | null {
    const deliveryState = this.taskMailDeliveryState;
    if (!deliveryState) return null;

    return {
      $implicit: deliveryState,
      text: this.taskMailDeliveryTooltip
    };
  }

  protected identify(_index: number, { id }: Task): number {
    return id;
  }

  protected toggleSubtasks(): void {
    const task = this.task();
    const scrollElement = document.querySelector('.base-panel') as HTMLElement | null;
    const initialScrollTop = scrollElement?.scrollTop ?? 0;

    task.openedSubtasks = !task.openedSubtasks;
    this.cdr.detectChanges();

    if (!scrollElement) {
      return;
    }

    const restoreScrollPosition = () => {
      scrollElement.scrollTop = initialScrollTop;
    };

    requestAnimationFrame(() => {
      restoreScrollPosition();
      requestAnimationFrame(restoreScrollPosition);
    });
    setTimeout(restoreScrollPosition, 0);
    setTimeout(restoreScrollPosition, 32);
  }

  protected dragStart(): void {
    this.closeStatusPanel();
    const sidenav = document.querySelector('.sidenav');
    if (sidenav) {
      sidenav.classList.add('d-none');
      setTimeout(() => sidenav.classList.remove('d-none'), 0);
    }

    this.dragging = true;
    this.startScrollInterval();
  }

  protected drag(event: DragEvent): void {
    this.mousePosition = { x: event.clientX, y: event.clientY };
  }

  protected dragEnd(): void {
    this.dragging = false;
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  private startScrollInterval(): void {
    const offset = 200;
    const speedOffset = 200;
    const scrollElement = document.querySelector('.base-panel') as HTMLElement | null;

    if (!scrollElement || this.interval) return;

    this.interval = setInterval(() => {
      if (!this.dragging) {
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }
        return;
      }

      const currentScrollTop = scrollElement.scrollTop;

      if (this.mousePosition.y > scrollElement.clientHeight - offset) {
        scrollElement.scrollTo(scrollElement.scrollLeft, currentScrollTop + speedOffset);
      }

      if (this.mousePosition.y < offset) {
        scrollElement.scrollTo(scrollElement.scrollLeft, currentScrollTop - speedOffset);
      }
    }, 200);
  }

  protected previewTask(event: MouseEvent): void {
    event.preventDefault();
    const task = this.task();
    if (!task?.taskId && !task?.id) return;

    const taskType = String(task?.type || '');
    if (
      !this.isProcessGroup() &&
      !!task?.project?.id &&
      (taskType === TaskType.TYPE_MESSAGE || taskType === TaskType.TYPE_FOLLOW_UP)
    ) {
      this.taskSidenavController.sidenav.open(
        ProcessMessageTaskSidenavComponent,
        this.taskSidenavController.getWideSidenavConfig(),
        {
          mode: 'send',
          projectId: task.project.id,
          stage: task?.project?.stage as any,
          sortOrder: Number(task.sortOrder || 1),
          parentId: task.parentId ?? null,
          groupId: this.groupId() ?? null,
          mapperTaskId: task.id,
          taskType,
          task
        }
      );
      return;
    }

    if (
      this.isProcessGroup() &&
      (taskType === TaskType.TYPE_MESSAGE || taskType === TaskType.TYPE_FOLLOW_UP)
    ) {
      this.taskClicked.emit({ task, groupId: this.groupId(), isProcessGroup: true });
      return;
    }

    this.ngZone.run(() => {
      this.taskSidenavController.previewTask(task?.taskId || task?.id?.toString(), {
        type: task?.type || TaskType.TYPE_TASK,
        isProcessTaskContext: this.isProcessGroup(),
        groupType: this.isProcessGroup() ? 'activity-template' : undefined,
        activityTemplateTaskType: this.isProcessGroup() ? task?.type || TaskType.TYPE_TASK : null
      });
    });
  }

  protected formatMetaDate(date: string | null | undefined): string {
    if (!date) return '';

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return '';

    const pad = (value: number) => value.toString().padStart(2, '0');

    return `${pad(parsedDate.getDate())}.${pad(parsedDate.getMonth() + 1)}.${parsedDate.getFullYear()} - ${pad(
      parsedDate.getHours()
    )}:${pad(parsedDate.getMinutes())}`;
  }

  protected onAssignSearchDropdownTaskChange(task: Task): void {
    Object.assign(this.task(), task);
    this.cdr.detectChanges();
  }

  protected emitRemoveTask(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.removeRequested.emit(this.task());
  }

  protected cancelScheduledMail(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const task = this.task();
    if (!task?.id || this.cancelScheduledMailLoading) return;

    this.cancelScheduledMailLoading = true;
    this.cdr.detectChanges();

    this.taskService.cancelTaskMail(task.id).subscribe({
      next: (response) => {
        task.mailScheduledAt = null;
        this.listTaskService.updateTask(task);
        this.snackBarService.success(this.getResponseMessage(response, 'Tasks.cancelScheduledMailSuccess'));
        this.cancelScheduledMailLoading = false;
        this.refreshView();
      },
      error: (error) => {
        this.snackBarService.error(this.getResponseMessage(error?.error, 'Tasks.cancelScheduledMailError'));
        this.cancelScheduledMailLoading = false;
        this.refreshView();
      }
    });
  }

  protected childStatus(task: Task): 'created' | 'completed' | 'cancelled' {
    if (task?.status === TaskStatus.COMPLETED) return 'completed';
    if (task?.status === TaskStatus.CANCELLED) return 'cancelled';
    return 'created';
  }

  protected getTaskTypeTranslationKey(): string {
    return getTaskTypeTranslationKey(this.taskType, this.isProcessGroup());
  }

  protected getTaskTypeCssClass(): string {
    return getTaskTypeCssClass(this.taskType);
  }

  protected getTaskTypeIcon(): string {
    return getTaskTypeIcon(this.taskType);
  }

  protected get orderedStatusOptions() {
    return this.taskStatusOptions;
  }

  protected getTaskStatusIcon(status: string): string {
    return getTaskStatusIcon(status);
  }

  protected getTaskStatusCssClass(status: string): string {
    return getTaskStatusCssClass(status);
  }

  protected getStatusOptionClasses(status: string, isCurrent: boolean): string[] {
    const classes = [this.getTaskStatusCssClass(status)];

    if (this.isStatusOptionActive(status)) {
      classes.push('list-tasks-single-task__status-panel__option--active');
    }

    if (isCurrent) {
      classes.push('list-tasks-single-task__status-panel__option--current');
    }

    return classes;
  }

  protected isStatusOptionActive(status: string): boolean {
    return this.currentTaskStatus === normalizeTaskStatus(status);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.statusPanelOpen) return;

    const target = event.target as Node | null;
    if (target && this.elementRef.nativeElement.contains(target)) {
      return;
    }

    this.closeStatusPanel();
  }

  protected onStatusTriggerClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.statusPanelOpen = !this.statusPanelOpen;
    console.log('this.statusPanelOpen', this.statusPanelOpen);
    this.cdr.detectChanges();
  }

  protected openStatusPanel(): void {
    if (this.statusPanelOpen) return;
    this.statusPanelOpen = true;
    this.cdr.detectChanges();
  }

  protected closeStatusPanel(): void {
    if (!this.statusPanelOpen) return;
    this.statusPanelOpen = false;
    this.cdr.detectChanges();
  }

  protected onStatusOptionSelected(status: string): void {
    const normalizedStatus = normalizeTaskStatus(status);
    this.closeStatusPanel();
    if (this.currentTaskStatus === normalizedStatus) return;

    if (this.shouldShowSubtasksStatusPopover) {
      this.pendingStatus = normalizedStatus;
      this.openSubtasksStatusConfirmation();
      return;
    }

    this.applyStatusChange(normalizedStatus, false);
  }

  private updateCompletedChildrenCount(task: Task | null): void {
    if (!task) return;
    task.completedChildrenCount = (task.children || []).filter(
      (child: Task) => child.status === TaskStatus.COMPLETED
    ).length;
  }

  private applyStatusChange(status: TaskStatus, applyStatusToSubtasks: boolean): void {
    const task = this.task();
    if (!task) return;

    task.status = status;

    if (applyStatusToSubtasks) {
      this.applyStatusToTaskTree(task, status);
      this.refreshVisibleSubtasks();
    }

    this.statusUpdated.emit({
      task,
      subtasksCompleted: applyStatusToSubtasks && status === TaskStatus.COMPLETED,
      applyStatusToSubtasks
    });
    this.updateCompletedChildrenCountRecursively(task);
    this.updateCompletedChildrenCount(this.parentTask());
    this.refreshView();
  }

  private applyStatusToTaskTree(task: Task, status: TaskStatus): void {
    (task.children || []).forEach((child: Task) => {
      child.status = status;
      this.applyStatusToTaskTree(child, status);
    });
  }

  private updateCompletedChildrenCountRecursively(task: Task): void {
    (task.children || []).forEach((child: Task) => this.updateCompletedChildrenCountRecursively(child));
    this.updateCompletedChildrenCount(task);
  }

  private refreshVisibleSubtasks(): void {
    this.subtasksComponents?.toArray().forEach((subtaskComponent: ListTasksSingleTaskComponent) => {
      subtaskComponent.refreshVisibleSubtasks();
      subtaskComponent.refreshView();
    });
  }

  private resetPendingStatusChange(): void {
    this.pendingStatus = null;
  }

  private openSubtasksStatusConfirmation(): void {
    const pendingStatus = this.pendingStatus;
    if (!pendingStatus) return;

    const { title, body } = this.getSubtasksStatusConfirmationContent(pendingStatus);

    this.dialog
      .open(ConfirmModalComponent, {
        width: Config.CONFIRM_MODAL_WIDTH,
        data: {
          title,
          body,
          cancelButtonText: 'ProjectTasks.Task.cancelCompletedSubtasks',
          submitButtonText: 'ProjectTasks.Task.confirmCompletedSubtasks',
          submitButtonType: ButtonTypes.NEW_PRIMARY_BLUE
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        const pendingStatus = this.pendingStatus;
        if (!pendingStatus) return;
        if (confirmed !== true && confirmed !== false) {
          this.resetPendingStatusChange();
          return;
        }

        this.applyStatusChange(pendingStatus, !!confirmed);
        this.resetPendingStatusChange();
      });
  }

  private getSubtasksStatusConfirmationContent(status: TaskStatus): { title: string; body: string } {
    switch (status) {
      case TaskStatus.COMPLETED:
        return {
          title: 'ProjectTasks.Task.doYouWantToCompletedSubtasksTitle',
          body: 'ProjectTasks.Task.doYouWantToCompletedSubtasks'
        };
      case TaskStatus.CANCELLED:
        return {
          title: 'ProjectTasks.Task.doYouWantToCanceledSubtasksTitle',
          body: 'ProjectTasks.Task.doYouWantToCanceledSubtasks'
        };
      default:
        return {
          title: 'ProjectTasks.Task.doYouWantToCreatedSubtasksTitle',
          body: 'ProjectTasks.Task.doYouWantToCreatedSubtasks'
        };
    }
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }

  private getResponseMessage(payload: any, fallbackKey: string): string {
    return payload?.message || this.translateService.instant(fallbackKey);
  }

  private get taskType(): string {
    return String(this.task()?.type || '');
  }
}
